import React, { Fragment, useEffect, useRef, useState } from 'react'

import valid from 'card-validator'
import { FieldData } from 'rc-field-form/lib/interface'
import styled from 'styled-components'

import { AppPlugin } from '@api/local'
import { Button, CreditCardDetails, Paragraph, Spacer } from '@client/components'
import { ResponsivePXValue, theme } from '@client/components/Theme'
import { useScrollIntoViewOnFocus } from '@hooks/UseScrollIntoViewOnFocus'
import { useScrollToTopOnMount } from '@hooks/UseScrollToTopOnMount'
import { Form, MonthYearInput, TextInput, useForm, CheckBox } from '@molecules/index'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;

  ${ResponsivePXValue('margin', { desktop: '0 16px' })}

  .submit-button {
    ${ResponsivePXValue('margin', '16px 0')}
  }
  .input-label {
    font-weight: 700;
  }
  input {
   border-color: ${theme.colors.whites.silver};
  }
`

const ActionContainer = styled.div<{ $stickyBottom?: boolean, $variant?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${(props) => props.$stickyBottom && `
    position: sticky;
    ${ResponsivePXValue('bottom', '8px')};
  `}

  ${(props) => props.$variant === 'horizontal' && `
    flex-direction: row;
    justify-content: flex-end;
    width: 100%;
  `}

  .card-action-button {
    ${ResponsivePXValue('width', { mobile: '100%', tablet: '180px', desktop: '180px' })};
  }
`
const InlineFields = styled.div`
  display: flex;
  gap: 12px;

  > div {
    flex: 1;
  }
`
export interface AddCardFormProps {
  onAddCard: (creditCardDetails: CreditCardDetails) => void
  actionButtonText?: string
}

interface AddCardState {
  loading: boolean
  cardType: string
  cvvLabel: string
  expiryMonth: string
  expiryYear: string
  isActionButtonDisabled: boolean
  isDefault: boolean
  actionButtonVariant: 'primary' | 'secondary'
  secondaryActionButtonVariant: 'primary' | 'secondary'
}

const DEFAULT_STATE: AddCardState = {
  loading: false,
  cardType: '',
  cvvLabel: 'CVV',
  expiryMonth: '',
  expiryYear: '',
  isActionButtonDisabled: true,
  isDefault: true,
  actionButtonVariant: 'secondary',
  secondaryActionButtonVariant: 'secondary',

}

export function AddCardForm({ onAddCard, actionButtonText = 'Save' }: AddCardFormProps): JSX.Element {

  const [form] = useForm()
  const [state, setState] = useState<AddCardState>({ ...DEFAULT_STATE })
  const isMobile = AppPlugin.shared().isMobile()
  const cardNicknameRef = useRef<HTMLInputElement | null>(null)

  const scrollRef = useRef(null)
  useScrollIntoViewOnFocus({ scrollContainer: scrollRef.current })
  useScrollToTopOnMount()

  useEffect(() => {
    const timer = setTimeout(() => {
      const input = document.querySelector('input[name="cardNickname"]') as HTMLInputElement | null
      if (input) input.focus()
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  const _handleSubmit = (data: CreditCardDetails) => {

    const details: CreditCardDetails = {
      cardNickname: data.cardNickname as string,
      nameOnCard: data.nameOnCard as string,
      cardNumber: data.cardNumber.replace(/\s/g, '') as string,
      cardType: data.cardType as string,
      expiryMonth: state.expiryMonth as string,
      expiryYear: state.expiryYear as string,
      cvv: data.cvv as string,
      isDefault: state.isDefault as boolean,
    }

    onAddCard(
      details,
    )

  }

  const _handleChange = (changedFields: FieldData[]) => {
    changedFields.forEach((field) => {
      (field.name as string[]).forEach((name) => {
        if (name === 'cardNumber') {
          let cardType = state.cardType
          const cardNum = field.value.replace(/\s/g, '')
          cardType = valid?.number(cardNum)?.card?.type
          const cvvLabel = cardType === 'american-express' ? 'CID' : 'CVV'
          setState((prevState) => ({
            ...prevState,
            cardType,
            cvvLabel,
          }))
        }
        if (name === 'expiry') {
          const expiryMonth = field.value.split('/')[0]
          const expiryYear = '20' + field.value.split('/')[1]
          setState((prevState) => ({
            ...prevState,
            expiryMonth,
            expiryYear,
          }))
        }
        if (name === 'isDefault') {
          const checked = !!(field.value && field.value.length > 0 && field.value[0])
          setState((prevState) => ({
            ...prevState,
            isDefault: checked,
          }))
        }
      })
    })
    const requiredFields = ['cardNickname', 'nameOnCard', 'cardNumber', 'expiry', 'cvv']
    const allRequiredFieldsTouched = form.isFieldsTouched(requiredFields, true)
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0)
    const isActionButtonDisabled = !allRequiredFieldsTouched || hasErrors
    const anyRequiredFieldTouched = requiredFields.some(field =>
      form.isFieldTouched(field),
    )

    const actionButtonVariant = isActionButtonDisabled ? 'secondary' : 'primary'
    const secondaryActionButtonVariant = anyRequiredFieldTouched ? 'primary' : 'secondary'

    setState((prevState) => ({
      ...prevState,
      isActionButtonDisabled,
      actionButtonVariant,
      secondaryActionButtonVariant,
    }))

  }

  // if disabled secondary else primary
  // add disabled config on clear, if any field is touched allow clear else not clear
  const headingText = 'PLEASE NOTE'
  const bodyText = 'We\'ll process a test transaction of R0 to confirm your details and get your plan started.  Remember, it\'s completely flexible - you can pause at any time.'
  return (
    <Fragment>
      <Container ref={scrollRef}>
        <Form form={form} onFinish={_handleSubmit} onFieldsChange={_handleChange} initialValues={{ isDefault: [true] }}>
          <TextInput name='cardNickname' ref={cardNicknameRef} placeholder='My Flush Card' label='Give your card a name' rules={[{ required: true, message: 'Please input a nickname' }]} />
          <TextInput name='nameOnCard' placeholder='Sam Samson' label='Enter your name as it appears on your card' rules={[{ required: true, message: 'Please input a name' }]} />
          <TextInput name='cardNumber' placeholder='0000 0000 0000 0000' variant='creditCard' label='Card number' rules={[{ required: true, message: 'Please input a card number' }]} />
          <InlineFields>
            <MonthYearInput
              name="expiry"
              label="Expiry Date"
              placeholder='MM/YY'
              absoluteError={true}
              rules={[
                { required: true, message: 'Please enter expiry date (MM/YY)' },
                {
                  validator: (_, value) => {
                    if (!value || !/^\d{2}\/\d{2}$/.test(value)) {
                      return Promise.reject(new Error('Invalid format — use MM/YY'))
                    }

                    const [mm] = value.split('/').map(Number)
                    if (mm < 1 || mm > 12) {
                      return Promise.reject(new Error('Invalid month — must be 01 to 12'))
                    }

                    return Promise.resolve()
                  },
                },
              ]}
            />
            <TextInput
              name='cvv'
              label={state.cvvLabel}
              maxLength={4}
              placeholder='000'
              onBlurError={true}
              absoluteError={true}
              rules={[
                { required: true, message: 'Please input a CVV' },
                {
                  validator: (_, value) => {
                    if (!/^\d{3,}$/.test(value)) {
                      return Promise.reject(new Error('CVV must be at least 3 digits'))
                    }
                    return Promise.resolve()
                  },
                },
              ]}
            />

          </InlineFields>
          <Spacer mobile='32px' desktop='40px' />
          <Paragraph transform='uppercase' variant='p3' className='heading-text' bold>
            {headingText}
          </Paragraph>
          <Spacer mobile='8px' desktop='16px' />
          <Paragraph variant='p5' color={theme.colors.greys.tundora}>
            {bodyText}
          </Paragraph>
          <Spacer mobile='8px' desktop='16px' />
          <CheckBox
            name="isDefault"
            options={[{ title: 'Set as default card', value: true }]}
            showLabel={false}
            showOptional={false}
            color="orange"
            variant="horizontal"
            value={[state.isDefault]}
          />
          <Spacer mobile='8px' desktop='12px' />
          <ActionContainer $variant={isMobile ? 'vertical' : 'horizontal'}>
            <Button
              className='card-action-button'
              variant={state.actionButtonVariant}
              loading={state.loading}
              disabled={state.loading || state.isActionButtonDisabled}
              color='black'
              title={actionButtonText}
              onClick={() => form.submit()} />
          </ActionContainer>
        </Form>
      </Container>
    </Fragment>
  )
}
